import axiosInstance from '../axios/axiosInstance'
import { REACT_APP_AUTH_API_DEV } from '../utils/config'

// Hand-mirrored from dev-backend/src/core/creator-performance/creator-performance.types.ts.
// Change both together.

export type RunStatus = 'idle' | 'running' | 'done' | 'failed'

export interface RunState {
  status: RunStatus
  step: string | null
  started_at: string | null
  finished_at: string | null
  error: string | null
}

export interface CategorySlice {
  category: string
  subscribers: number
  revenue: number
}

export interface LtvFields {
  subscribers: number
  revenue: number
  avg_revenue: number
  monthly_share: number
  cohort_age_months: number
  maturity: number
  projected_obs_ltv: number
  category_prior_ltv: number
  shrinkage_w: number
  ltv_star: number
  ltv_star_p25: number
  ltv_star_p75: number
  expected_to_spend_p25: number
  expected_to_spend_p50: number
  expected_to_spend_p75: number
  recent_subscribers_90d: number
  recent_revenue_90d: number
}

/** Expected value of signups that have not paid yet ("pipeline"). */
export interface PipelineFields {
  signup_conversion_rate: number
  signup_conversion_rate_p25: number
  expected_new_payers: number
  expected_new_payers_p25: number
  /** Conservative: the figure the page shows. */
  pipeline_value_p25: number
  pipeline_value_p50: number
  value_per_new_signup_p25: number
}

/** With payers: ETS p50. With none yet: the conservative signup value. */
export interface WorthFields {
  worth: number
  worth_basis: 'ets_p50' | 'signups'
}

export interface ChannelRow extends LtvFields, PipelineFields, WorthFields {
  category: string
  channel: string
  category_breakdown: CategorySlice[]
  signups: number
  paid_from_signups: number
}

export interface CategoryRow extends LtvFields, PipelineFields, WorthFields {
  category: string
  signups: number
  paid_from_signups: number
}

export interface PartnerRow {
  name: string
  handle: string
  in_channel_table: boolean
  signups: number
  paid_from_signups: number
  subscribers: number
  revenue: number
  conversion: number
  ltv_star: number | null
  ltv_star_p25: number | null
  ltv_star_p75: number | null
  expected_to_spend_p25: number | null
  expected_to_spend_p50: number | null
  expected_to_spend_p75: number | null
  recent_subscribers_90d: number
  expected_new_payers: number
  expected_new_payers_p25: number
  pipeline_value_p25: number
  pipeline_value_p50: number
  value_per_new_signup_p25: number
  worth: number
  worth_basis: 'ets_p50' | 'signups'
}

export interface CreatorPerformanceSettings {
  /** Global LTV per payer (web and app), HKD, recomputed on every refresh. */
  lifetime_value: number
  cac_to_ltv: number
  payback_fraction: number
  prior_shrinkage_k: number
  prior_relative_uncertainty: number
  maturity_reference_age_months: number
}

export interface StoreStatus {
  status: 'counted' | 'not_configured' | 'failed'
  checked: number
  read: number
  unconverted: number
  error?: string
}

export interface CreatorPerformancePayload {
  snapshot_date: string
  currency: 'HKD'
  settings: CreatorPerformanceSettings
  totals: {
    total_paid_subscribers: number
    total_revenue: number
    matched_paid_subscribers: number
    matched_from_in_app: number
    matched_from_backfill: number
    matched_from_hints: number
    upgraded_by_hints: number
    unmatched_paid_subscribers: number
    unmatched_paid_revenue: number
    survey_answers: number
    test_accounts_excluded: number
  }
  /** Absent on refreshes from before app payers were counted. */
  stores?: Record<'app_store' | 'google_play', StoreStatus>
  maturity_curves: Record<'all' | 'annual' | 'monthly', Record<string, number>>
  signup_conversion: {
    baseline_rate: number
    window_rates: Record<string, number>
    prior_strength: number
    curve: Record<string, number>
  }
  by_channel: ChannelRow[]
  by_category: CategoryRow[]
  partners: PartnerRow[]
}

export interface HistoryChannel {
  /** Channel name, or "<category>/Unspecified" for unnamed answers. */
  key: string
  subscribers: number
  revenue: number
  ltv_star: number
  expected_to_spend_p50: number
  signups: number
  paid_from_signups: number
  pipeline_value_p25: number
}

export interface HistoryCategory {
  category: string
  subscribers: number
  revenue: number
  ltv_star: number
  signups: number
  pipeline_value_p25: number
}

export interface HistoryEntry {
  generated_at: string
  snapshot_date: string
  global_ltv: number
  matched_paid_subscribers: number
  total_revenue: number
  channels: HistoryChannel[]
  categories: HistoryCategory[]
}

export interface HistorySummary {
  generated_at: string
  snapshot_date: string
  global_ltv: number
  matched_paid_subscribers: number
  total_revenue: number
}

export interface CreatorPerformanceState {
  run: RunState
  generated_at: string | null
  payload: CreatorPerformancePayload | null
  /** The refresh before the current one. Null until there are two. */
  previous: HistoryEntry | null
  /** Newest first. */
  history: HistorySummary[]
}

/** Key that matches a channel row to its history entries. */
export const historyKey = (row: { channel: string; category: string }): string =>
  row.channel === 'Unspecified' ? `${row.category}/Unspecified` : row.channel

export const getCreatorPerformance = async (): Promise<CreatorPerformanceState> => {
  const res = await axiosInstance.get(`${REACT_APP_AUTH_API_DEV}creator-performance`)
  return res?.data
}

/**
 * Starts a refresh. A 409 means one is already running, which is not a failure
 * from the page's point of view: it carries the live run, so return it and let
 * the caller start polling.
 */
export const refreshCreatorPerformance = async (): Promise<{ run: RunState }> => {
  try {
    const res = await axiosInstance.post(`${REACT_APP_AUTH_API_DEV}creator-performance/refresh`)
    return res?.data
  } catch (e: any) {
    if (e?.response?.status === 409 && e.response.data?.run) return e.response.data
    throw e
  }
}
